import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import LandingPage from '@/pages/LandingPage';
import Pricing from '@/pages/Pricing';
import Dashboard from '@/pages/Dashboard';
import Organizations from '@/pages/Organizations';
import Messages from '@/pages/Messages';
import Opportunities from '@/pages/Opportunities';
import Events from '@/pages/Events';
import Profile from '@/pages/Profile';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth
const api = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export { api };

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage onLogin={login} />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/organizations"
            element={user ? <Organizations user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/messages"
            element={user ? <Messages user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/opportunities"
            element={user ? <Opportunities user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/events"
            element={user ? <Events user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={user ? <Profile user={user} onLogout={logout} /> : <Navigate to="/" />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;